import React from "react";

import BuildDuration from "app/components/build/Duration";
import BuildMessage from "app/components/build/Message";
import BuildState from "app/components/icons/BuildState";
import FriendlyTime from "app/components/shared/FriendlyTime";
import Icon from "app/components/shared/Icon";
import { shortCommit } from "app/lib/commits";
import BuildsStore from "app/stores/BuildsStore";

import { Job } from "../Header/pipeline";
import Steps from "../Header/pipeline/Steps";
import { track } from "app/lib/segmentAnalytics";
import BuildRowNext from "./BuildRowNext";

// TODO: component can be removed along with the feature flag and merged back into BuildRow
function Row({ children, href }: { children: React.ReactNode; href: string }) {
  if (window.Features.BuildsFilteringClickTargets) {
    return (
      <div className="build-row-header flex items-center px3 py2.5">
        {children}
      </div>
    );
  }

  return (
    <a
      href={href}
      className="build-row-header text-decoration-none black hover-black flex items-center px3 py2.5"
    >
      {children}
    </a>
  );
}

const BuildRow = ({
  build,
  store,
}: {
  build: {
    id: string;
    authorName: string;
    blockedState: string | null;
    branchName: string;
    branchPath: string;
    canceledAt: string | null;
    commitId: string;
    commitShortLength: number;
    commitUrl: string;
    createdAt: string;
    finishedAt: string | null;
    message: string;
    number: number;
    path: string;
    scheduledAt: string | null;
    startedAt: string | null;
    state:
      | "passed"
      | "failing"
      | "failed"
      | "blocked"
      | "canceled"
      | "canceling"
      | "creating"
      | "skipped"
      | "not_run"
      | "scheduled"
      | "started";
    jobs?: Array<Job>;
    jobsCount?: number;
    project: {
      provider: {
        frontendIcon: string;
      };
    };
  };
  store: BuildsStore;
}) => {
  if (window.Features.BuildsFilteringNewBuildsList) {
    return <BuildRowNext build={build} store={store} />;
  }

  const buildStateForNewFrontend = () => {
    // TODO: The new frontend gear expects "running" (as per the GraphQL API) but the
    // React presenters and JSON endpoints still return "started"
    //
    // This can be `rm -rf`'d when it's moved across to GraphQL
    if (build.state === "started") {
      return "RUNNING";
    }
    return build.state.toUpperCase();
  };

  const getBlockedState = () => {
    const { blockedState } = build;

    if (blockedState === null || blockedState === undefined) {
      return blockedState;
    }

    return blockedState.toUpperCase();
  };

  const sha = shortCommit(build.commitId, build.commitShortLength);
  const seperator = <span>&middot;</span>;

  const canShowSteps = window.Features.BuildsFilteringShowSteps && build.jobs;
  const [showSteps, setShowSteps] = React.useState(false);

  function toggleSteps(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.currentTarget.blur();

    track("Builds List Toggle Steps Clicked", {
      action: showSteps ? "Hide" : "Show",
      text: showSteps
        ? `Hide steps for #${build.number}`
        : `Show steps for #${build.number}`,
      source: "Build Row",
    });

    setShowSteps(!showSteps);
  }

  return (
    <div className="build-row border-gray antialiased">
      <Row href={build.path}>
        <div className="flex-none mr3">
          <BuildState.Regular
            state={buildStateForNewFrontend()}
            blockedState={getBlockedState()}
            className="block"
          />
        </div>

        <div className="flex flex-auto xs-flex-wrap items-center gapy1 gapx3">
          <div className="flex-auto flex flex-column gap0.5">
            <div className="mb0.5 truncate">
              {window.Features.BuildsFilteringClickTargets ? (
                <a className="charcoal-900 semi-bold" href={build.path}>
                  <BuildMessage message={build.message} />
                </a>
              ) : (
                <span className="charcoal-900 semi-bold">
                  <BuildMessage message={build.message} />
                </span>
              )}
            </div>

            <span className="flex flex-wrap gap1.5 charcoal-600 h6 m0 line-height-1 regular items-center">
              <span>{build.authorName}</span>

              {seperator}

              <span>#{build.number}</span>
              {seperator}

              <a
                data-turbo="true"
                href={build.branchPath}
                className="flex items-center color-inherit hover-color-inherit"
              >
                <Icon
                  icon="custom/outline/14x/branch"
                  style={{ height: 14, width: 14 }}
                />
                {build.branchName}
              </a>

              {seperator}

              <a
                href={build.commitUrl}
                target="_blank"
                className="monospace line-height-1 flex items-center color-inherit hover-color-inherit"
                rel="noreferrer"
              >
                <Icon
                  icon={build.project.provider.frontendIcon}
                  className="mr1"
                  style={{ height: 14, width: 14 }}
                />
                {sha}
              </a>

              {seperator}

              <span>
                Created <FriendlyTime value={build.createdAt} />
              </span>
            </span>
          </div>

          <span
            className="charcoal-600 nowrap line-height-1"
            style={{ fontSize: 13 }}
          >
            <BuildDuration
              build={{
                state: build.state,
                scheduledAt: build.scheduledAt,
                startedAt: build.startedAt,
                canceledAt: build.canceledAt,
                finishedAt: build.finishedAt,
              }}
              durationFormat="Micro"
            />
          </span>
        </div>

        {canShowSteps && (
          <button
            onClick={toggleSteps}
            className="unstyled-button charcoal-300 hover-charcoal-800 p1 -mr0.5"
            aria-label={showSteps ? "Hide steps" : "Show steps"}
          >
            <span className="hide">
              {showSteps
                ? `Hide steps for #${build.number}`
                : `Show steps for #${build.number}`}
            </span>

            <Icon
              icon="heroicons/20/solid/chevron-up-down"
              style={{ height: 20, width: 20 }}
            />
          </button>
        )}
      </Row>

      {showSteps && (
        <div
          className="build-row-steps flex flex-wrap flex-auto gap1 px3 py2.5 border-top border-gray-400 bg-slate-100 shadow-inset"
          style={{ paddingLeft: 55 }}
        >
          <Steps build={build} store={store} small={true} />
        </div>
      )}
    </div>
  );
};

export default BuildRow;
